import React, { useState, useEffect } from 'react';
import {
  Settings, Save, Database, Mail, Bell, Shield,
  Server, Activity, AlertTriangle, CheckCircle, XCircle,
  Globe, Lock, Key, Download, Upload,
  Clock, Users, Building, TrendingUp, Monitor,
  Wrench, Phone, MessageSquare, Eye, EyeOff,
  RefreshCw, X
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { adminApi, SystemSettings, SystemSettingsUpdate } from '@/api/admin';
import toast from 'react-hot-toast';

const inputCls = "w-full px-3 py-2 border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none";
const labelCls = "block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5";
const checkboxCls = "border-slate-300 text-primary-600 focus:ring-primary-500";

const SystemSettingsPage = () => {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('general');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Fetch settings from API
  const fetchSettings = async () => {
    try {
      setError(null);
      const response = await adminApi.settings.get();
      setSettings(response);
    } catch (err: any) {
      const errorMessage = err.message || 'Sistem ayarları yüklenirken hata oluştu';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error fetching settings:', err);
    }
  };

  // Initial load
  useEffect(() => {
    const loadSettings = async () => {
      setIsLoading(true);
      try {
        await fetchSettings();
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, []);

  // Refresh data
  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      await fetchSettings();
      toast.success('Ayarlar güncellendi');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      const updatedSettings = await adminApi.settings.update(settings as unknown as SystemSettingsUpdate);
      setSettings(updatedSettings);
      setUnsavedChanges(false);
      toast.success('Ayarlar başarıyla kaydedildi');
    } catch (err: any) {
      const errorMessage = err.message || 'Ayarlar kaydedilirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error saving settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Test email settings
  const testEmailSettings = async () => {
    if (!settings?.email.from_email) {
      toast.error('Lütfen önce e-posta ayarlarını kaydedin');
      return;
    }

    try {
      const result = await adminApi.settings.testEmail(settings.email.from_email);
      if (result.success) {
        toast.success('Test e-postası başarıyla gönderildi');
      } else {
        toast.error(result.message || 'Test e-postası gönderilemedi');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Test e-postası gönderilirken hata oluştu';
      toast.error(errorMessage);
      console.error('Error testing email:', err);
    }
  };

  // Handle settings change
  const handleSettingChange = (section: keyof SystemSettings, key: string, value: any) => {
    if (!settings) return;

    setSettings(prev => ({
      ...prev!,
      [section]: {
        ...prev![section],
        [key]: value
      }
    }));
    setUnsavedChanges(true);
  };

  // Handle array settings change
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleArraySettingChange = (section: keyof SystemSettings, key: string, values: string[]) => {
    if (!settings) return;

    setSettings(prev => ({
      ...prev!,
      [section]: {
        ...prev![section],
        [key]: values
      }
    }));
    setUnsavedChanges(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-2 border-primary-100 border-t-primary-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Sistem ayarları yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-danger-500 mx-auto mb-4" />
          <h3 className="text-lg font-serif font-medium text-slate-900 mb-2">Ayarlar Yüklenemedi</h3>
          <p className="text-slate-500 mb-4">Sistem ayarları yüklenirken bir hata oluştu.</p>
          <Button onClick={refreshData}>Tekrar Dene</Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', name: 'Genel', icon: Settings },
    { id: 'email', name: 'E-posta', icon: Mail },
    { id: 'security', name: 'Güvenlik', icon: Shield }
    // Bakım / API / Bildirimler / VoIP / Sistem sekmeleri hiç uygulanmadı;
    // gösterildiklerinde yalnızca bir "yakında" mesajı çıkıyordu. Arayüz
    // çalışmayan sekme reklamı yapmasın diye listeden çıkarıldılar.
    // VoIP ayarları zaten kendi sayfasında (VoIPSettings.tsx).
  ];

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {error && (
        <div className="bg-danger-50 border border-danger-200 p-4 flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-danger-600 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-danger-700">Hata</h3>
              <p className="text-sm text-danger-700">{error}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setError(null)}
            className="text-danger-600 hover:text-danger-800"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-5 border-b border-slate-200">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary-600 mb-1">Verwaltung</p>
          <h1 className="text-2xl font-serif font-medium text-slate-900">Sistem Ayarları</h1>
          <p className="text-slate-600 mt-1">Platformun genel ayarlarını yönetin</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={refreshData} disabled={isRefreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Yenile
          </Button>
          <Button
            variant="default"
            onClick={saveSettings}
            disabled={!unsavedChanges || isSaving}
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      {unsavedChanges && (
        <div className="bg-yellow-50 border border-yellow-200 p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-yellow-700">Kaydedilmemiş Değişiklikler</h3>
              <p className="text-sm text-yellow-700">Yaptığınız değişiklikler henüz kaydedilmedi. Sayfadan çıkmadan önce kaydetmeyi unutmayın.</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 border-t-2 border-t-primary-600">
        {/* Tab Navigation */}
        <div className="border-b border-slate-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center transition-colors`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* General Settings */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-serif font-medium text-slate-900 mb-4">Genel Ayarlar</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls}>
                      Site Adı
                    </label>
                    <input
                      type="text"
                      value={settings.general.site_name}
                      onChange={(e) => handleSettingChange('general', 'site_name', e.target.value)}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      Destek E-postası
                    </label>
                    <input
                      type="email"
                      value={settings.general.support_email}
                      onChange={(e) => handleSettingChange('general', 'support_email', e.target.value)}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      Destek Telefonu
                    </label>
                    <input
                      type="tel"
                      value={settings.general.support_phone}
                      onChange={(e) => handleSettingChange('general', 'support_phone', e.target.value)}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      Oturum Zaman Aşımı (dakika)
                    </label>
                    <input
                      type="number"
                      value={settings.general.session_timeout}
                      onChange={(e) => handleSettingChange('general', 'session_timeout', parseInt(e.target.value))}
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <label className={labelCls}>
                    Site Açıklaması
                  </label>
                  <textarea
                    value={settings.general.site_description}
                    onChange={(e) => handleSettingChange('general', 'site_description', e.target.value)}
                    rows={3}
                    className={inputCls}
                  />
                </div>

                <div className="mt-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={settings.general.maintenance_mode}
                      onChange={(e) => handleSettingChange('general', 'maintenance_mode', e.target.checked)}
                      className={checkboxCls}
                    />
                    <span className="ml-2 text-sm text-slate-700">Bakım Modu</span>
                  </label>
                  <p className="text-xs text-slate-500 mt-1">
                    Etkinleştirildiğinde sadece adminler siteye erişebilir
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Email Settings */}
          {activeTab === 'email' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-serif font-medium text-slate-900 mb-4">E-posta Ayarları</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls}>
                      SMTP Host
                    </label>
                    <input
                      type="text"
                      value={settings.email.smtp_host}
                      onChange={(e) => handleSettingChange('email', 'smtp_host', e.target.value)}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      SMTP Port
                    </label>
                    <input
                      type="number"
                      value={settings.email.smtp_port}
                      onChange={(e) => handleSettingChange('email', 'smtp_port', parseInt(e.target.value))}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      SMTP Kullanıcı Adı
                    </label>
                    <input
                      type="text"
                      value={settings.email.smtp_user}
                      onChange={(e) => handleSettingChange('email', 'smtp_user', e.target.value)}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      SMTP Şifre
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={settings.email.smtp_password}
                        onChange={(e) => handleSettingChange('email', 'smtp_password', e.target.value)}
                        className={inputCls + " pr-10"}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>
                      Gönderen E-posta
                    </label>
                    <input
                      type="email"
                      value={settings.email.from_email}
                      onChange={(e) => handleSettingChange('email', 'from_email', e.target.value)}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      Gönderen Ad
                    </label>
                    <input
                      type="text"
                      value={settings.email.from_name}
                      onChange={(e) => handleSettingChange('email', 'from_name', e.target.value)}
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.email.smtp_use_tls}
                        onChange={(e) => handleSettingChange('email', 'smtp_use_tls', e.target.checked)}
                        className={checkboxCls}
                      />
                      <span className="ml-2 text-sm text-slate-700">TLS Kullan</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.email.smtp_use_ssl}
                        onChange={(e) => handleSettingChange('email', 'smtp_use_ssl', e.target.checked)}
                        className={checkboxCls}
                      />
                      <span className="ml-2 text-sm text-slate-700">SSL Kullan</span>
                    </label>
                  </div>

                  <div className="pt-4">
                    <Button
                      variant="outline"
                      onClick={testEmailSettings}
                      className="flex items-center"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Test E-postası Gönder
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-serif font-medium text-slate-900 mb-4">Güvenlik Ayarları</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls}>
                      Minimum Şifre Uzunluğu
                    </label>
                    <input
                      type="number"
                      min="6"
                      max="50"
                      value={settings.security.password_min_length}
                      onChange={(e) => handleSettingChange('security', 'password_min_length', parseInt(e.target.value))}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      Maksimum Giriş Denemesi
                    </label>
                    <input
                      type="number"
                      min="3"
                      max="10"
                      value={settings.security.max_login_attempts}
                      onChange={(e) => handleSettingChange('security', 'max_login_attempts', parseInt(e.target.value))}
                      className={inputCls}
                    />
                  </div>

                  <div>
                    <label className={labelCls}>
                      Hesap Kilitleme Süresi (dakika)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="1440"
                      value={settings.security.login_lockout_duration}
                      onChange={(e) => handleSettingChange('security', 'login_lockout_duration', parseInt(e.target.value))}
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Şifre Gereksinimleri</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.security.password_require_uppercase}
                        onChange={(e) => handleSettingChange('security', 'password_require_uppercase', e.target.checked)}
                        className={checkboxCls}
                      />
                      <span className="ml-2 text-sm text-slate-700">Büyük harf zorunlu</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.security.password_require_lowercase}
                        onChange={(e) => handleSettingChange('security', 'password_require_lowercase', e.target.checked)}
                        className={checkboxCls}
                      />
                      <span className="ml-2 text-sm text-slate-700">Küçük harf zorunlu</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.security.password_require_numbers}
                        onChange={(e) => handleSettingChange('security', 'password_require_numbers', e.target.checked)}
                        className={checkboxCls}
                      />
                      <span className="ml-2 text-sm text-slate-700">Rakam zorunlu</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.security.password_require_symbols}
                        onChange={(e) => handleSettingChange('security', 'password_require_symbols', e.target.checked)}
                        className={checkboxCls}
                      />
                      <span className="ml-2 text-sm text-slate-700">Özel karakter zorunlu</span>
                    </label>
                  </div>

                  <div className="pt-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.security.two_factor_enabled}
                        onChange={(e) => handleSettingChange('security', 'two_factor_enabled', e.target.checked)}
                        className={checkboxCls}
                      />
                      <span className="ml-2 text-sm text-slate-700">İki Faktörlü Kimlik Doğrulama</span>
                    </label>
                    <p className="text-xs text-slate-500 mt-1 ml-6">
                      Etkinleştirildiğinde kullanıcılar giriş yaparken ek doğrulama yapacak
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SystemSettingsPage;
